/**
 * Announcer subtitle (DESIGN-client §7): `presentation.subtitle` until `until`; hidden when
 * subtitles are off.
 *
 * ## Where it sits
 *
 * In the gap between the trick and the player's own cards, measured from the scene's published
 * anchors rather than guessed at in CSS.
 *
 * It used to sit a fixed distance off the bottom of the viewport, which put it on top of the fan in
 * portrait. Moving it to the fan clearance fixed that and immediately created the opposite problem:
 * at 1280x720 the trick pile reaches down to where the fan begins, so a bark landed on the played
 * cards instead. There is no single fixed offset that clears both, because the size of the gap
 * depends on the viewport and on the fan's lift.
 *
 * So the rule is a reserved band rather than an offset: the box is given the largest strip of screen
 * that nothing else has claimed, and it is sized to fit that strip. Covering the felt is fine.
 * Covering a card, a score panel or a control is not.
 *
 * What it must clear, and why each one is on the list:
 *   - `hand.zone`   the player's own fan. The original bug.
 *   - `trick.center` the played cards. The bug created by fixing the first one.
 *   - `shelf.0/1`   the two book shelves, which carry the running trick count.
 *   - `panel.*`     the Pixi team panels, once the scene publishes them — the score lives there now.
 *   - `.hud__top`   the Hand N/4 pill and its timer bar.
 *   - `.hud__bottom` the time bank, "?", gear and emote cluster. On a 390x844 phone this sits at
 *     y 550..650 while the fan starts at 658, so "just above the fan" is exactly where it is: the
 *     bark covered the "?" and the gear in the production screenshot this was written from.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useStore } from '../../store/store';

/** Breathing room between the subtitle and whatever it is avoiding. */
const GAP = 8;

export function Subtitle() {
  const subtitle = useStore((s) => s.presentation.subtitle);
  const enabled = useStore((s) => s.settings.subtitles);
  const anchors = useStore((s) => s.presentation.anchors);
  /*
   * Read so that this component re-renders when the HUD's top row appears, not to gate anything.
   *
   * Two of the obstacles below are measured out of the DOM, and `.hud__top` is only in the DOM while
   * a bid is not live. Nothing else here changes when that flips, so without this read the box was
   * placed against whichever HUD happened to exist on the frame the bark arrived and then kept that
   * position for its whole life. It showed up as a bark at the top of the screen, sitting on the
   * hand counter, on any bark that landed in the same tick the bid ended.
   */
  const bidLive = useStore((s) => s.presentation.bidLive);
  const [, tick] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [hud, setHud] = useState<{ top: Band | null; bottom: Band | null }>({ top: null, bottom: null });

  useEffect(() => {
    if (!subtitle) return;
    const ms = Math.max(0, subtitle.until - Date.now());
    const t = setTimeout(() => tick((n) => n + 1), ms + 20);
    return () => clearTimeout(t);
  }, [subtitle]);

  /*
   * Everything measured, in one place, after commit and before paint.
   *
   * The box's own size, because the placement depends on how tall the line wrapped to and how wide
   * the box is, and one that jumped into position after its first frame would glitch on every bark.
   * A ref read during render is null on the first pass, and a zero width made every obstacle miss
   * the box's column — so it found nothing to avoid and fell back to the stylesheet, which is the
   * position on the fan this was written to stop using.
   *
   * The HUD clusters, because they are DOM: they move with their own safe-area vars, and the top row
   * is unmounted entirely while a bid is live. Reading them during render read them one commit too
   * early — on the frame a bid ended, `.hud__top` was not in the document yet, the box was placed in
   * the space it was about to occupy, and it stayed there for the rest of the bark.
   */
  useLayoutEffect(() => {
    const r = boxRef.current?.getBoundingClientRect();
    if (r && r.height > 0) setSize((prev) => (prev.w === r.width && prev.h === r.height ? prev : { w: r.width, h: r.height }));
    const read = (sel: string): Band | null => {
      const e = document.querySelector(sel)?.getBoundingClientRect();
      return e && e.height > 0 ? { top: e.top, bottom: e.bottom, left: e.left, right: e.right } : null;
    };
    const next = { top: read('.hud__top'), bottom: read('.hud__bottom') };
    setHud((prev) => (sameBand(prev.top, next.top) && sameBand(prev.bottom, next.bottom) ? prev : next));
  });

  if (!enabled || !subtitle || subtitle.until <= Date.now()) return null;

  const vh = typeof window === 'undefined' ? 0 : window.innerHeight;

  /*
   * The fan reaches the bottom of the screen, whatever its anchor says.
   *
   * `hand.zone` is `hand.cy - card.h - lift` by `card.h + lift`, so its bottom edge is the fan's
   * centre line. The fan is an arc: the outer cards hang well below that, and at 1280x720 a bark
   * placed in the "gap" under the anchor was drawn straight across the bottom of the player's own
   * cards while every rectangle test said it was clear.
   *
   * Extending it to the foot of the window is right rather than merely safe. There is nothing under
   * the fan to put a subtitle on — it is the last thing on the screen — so the only placements this
   * removes are the wrong ones.
   */
  const fan = band(anchors['hand.zone']);
  const style = placeSubtitle({
    obstacles: [
      fan && vh > 0 ? { ...fan, bottom: Math.max(fan.bottom, vh) } : fan,
      band(anchors['trick.center']),
      band(anchors['shelf.0']),
      band(anchors['shelf.1']),
      ...panelBands(anchors),
      /*
       * The local player's own seat, disc AND name.
       *
       * `seat.0.box` is `solveLayout.seatPaintedBox`, the same rect the shelf and bid solvers avoid.
       * The disc-only `seat.0` anchor is not enough: at 390x844 the box landed in the strip between
       * the felt and the controls, which is exactly where that seat's avatar and name are, and the
       * bark was drawn across both (`docs/qa/panel-mobile.png`).
       *
       * Only the local seat. The other three sit on the felt with the trick between them, and adding
       * them closes the last band a phone has.
       */
      band(anchors['seat.0.box']),
      // Both measured in the layout effect above. `.hud__top` is absent from the DOM during a bid by
      // design — the scene's prompt owns that band then — and `bidLive` is read so that this
      // component re-renders, and re-measures, on the frame it comes back.
      bidLive ? null : hud.top,
      hud.bottom,
    ],
    height: size.h,
    width: size.w,
    viewportWidth: typeof window === 'undefined' ? 0 : window.innerWidth,
    viewportHeight: vh,
  });

  return (
    <div
      ref={boxRef}
      className="subtitle"
      role="status"
      data-testid="subtitle"
      // Until it has been measured the box is laid out but not shown, so the first frame cannot be
      // the one in the wrong place. `visibility` rather than `display`, so it still has a height.
      style={{ ...style, ...(size.h === 0 ? { visibility: 'hidden' as const } : {}) }}
    >
      {subtitle.text}
    </div>
  );
}

/** A rectangle in viewport space. Anchors arrive as `DOMRect`, the HUD as a measured rect. */
export interface Band {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

const band = (r: { x: number; y: number; width: number; height: number } | undefined | null): Band | null =>
  r ? { top: r.y, bottom: r.y + r.height, left: r.x, right: r.x + r.width } : null;

/** Whether two measured rects are the same, so a re-measure that found nothing new costs no render. */
const sameBand = (a: Band | null, b: Band | null): boolean =>
  a === b || (a !== null && b !== null && a.top === b.top && a.bottom === b.bottom && a.left === b.left && a.right === b.right);

/**
 * The Pixi team panels, by prefix rather than by name.
 *
 * The score moved out of the DOM and onto the felt, so the panels are now the one place a player
 * reads it and a bark over one is the same defect as a bark over a card. Matching `panel.` rather
 * than two literal keys means this keeps working whether the scene publishes `panel.0`/`panel.1`,
 * `panel.us`/`panel.them`, or a bracket sub-anchor alongside them, and it costs nothing while the
 * scene publishes none of them.
 */
export function panelBands(anchors: Record<string, DOMRect> | undefined): Band[] {
  if (!anchors) return [];
  const out: Band[] = [];
  for (const key of Object.keys(anchors)) {
    if (!key.startsWith('panel.')) continue;
    const b = band(anchors[key]);
    if (b) out.push(b);
  }
  return out;
}

/**
 * Where the subtitle's bottom edge goes, as a `bottom` offset in px.
 *
 * The rule is not "above the fan" or "above the trick" — it is "in a gap that nothing else is
 * using". Both earlier rules were single obstacles dressed up as a policy, and each one moved the
 * box onto whatever it had not been told about: first the fan, then the trick, then the books shelf
 * and the HUD's own control cluster, which on a phone sit exactly where the fan clearance points.
 *
 * So: take every rectangle the subtitle could collide with, drop the ones it does not overlap
 * horizontally, and place the box in a vertical gap between what is left. The lowest gap that fits
 * wins — near the player's own cards is where they are already looking — and the largest gap is the
 * fallback when nothing fits, because a bark half over a shelf still beats one off the screen.
 *
 * Returns an empty style when the scene has published nothing to avoid, and the stylesheet's own
 * fallback applies: the dev pages, and the first moment of a cold load.
 */
export function placeSubtitle(opts: {
  /** Everything the box must not cover: the fan, the trick, both shelves, the HUD clusters. */
  obstacles: ReadonlyArray<Band | null>;
  height: number;
  width: number;
  viewportWidth: number;
  viewportHeight: number;
}): { bottom?: string } {
  const { height: h, width: w, viewportWidth: vw, viewportHeight: vh } = opts;
  if (h <= 0 || vh <= 0) return {};

  // The box is centred, so only obstacles that overlap that column can be in its way. Without this
  // the HUD's bottom-right cluster would push the subtitle around on a desktop it never touches.
  const boxLeft = (vw - w) / 2;
  const boxRight = boxLeft + w;
  const blocking = opts.obstacles
    .filter((b): b is Band => b !== null && b.right > boxLeft && b.left < boxRight && b.bottom > b.top)
    .sort((a, b) => a.top - b.top);
  if (blocking.length === 0) return {};

  // Merge overlapping obstacles, then read the gaps between them.
  const merged: Band[] = [];
  for (const b of blocking) {
    const last = merged[merged.length - 1];
    if (last && b.top <= last.bottom) last.bottom = Math.max(last.bottom, b.bottom);
    else merged.push({ ...b });
  }

  const GAPS: Array<{ top: number; bottom: number }> = [];
  let cursor = 0;
  for (const b of merged) {
    if (b.top - cursor > 0) GAPS.push({ top: cursor, bottom: b.top });
    cursor = Math.max(cursor, b.bottom);
  }
  if (vh - cursor > 0) GAPS.push({ top: cursor, bottom: vh });

  /*
   * Lowest that fits, in three tiers, and the tiers are the whole design.
   *
   * Lowest, because the player is looking at their own cards and a bark read down there is read
   * without moving their eyes off the game. Three tiers, because a phone rarely has a band with room
   * to spare: at 360x780 the free strips are 28, 53 and 52 pixels tall against a two-line box of
   * about 47, so insisting on `h + GAP` finds nothing and falling straight to "largest" sent the box
   * to the top of the table — past a strip that would have held it.
   *
   * So: the lowest band with clearance; failing that the lowest band the box simply fits in; failing
   * that the largest band there is, where it will overlap something and the least-bad choice is the
   * one that overlaps least. `GAP` is a preference, not a constraint.
   */
  const lowestWith = (room: number): { top: number; bottom: number } | undefined => {
    const fits = GAPS.filter((g) => g.bottom - g.top >= room);
    return fits[fits.length - 1];
  };
  const chosen =
    lowestWith(h + GAP) ??
    lowestWith(h) ??
    GAPS.reduce((a, b) => (b.bottom - b.top > a.bottom - a.top ? b : a), GAPS[0] ?? { top: 0, bottom: vh });
  const slack = chosen.bottom - chosen.top - h;

  // Roomy gap: sit near its bottom, close to the cards the player is already looking at. Tight gap:
  // centre it, so the few pixels of clearance are shared rather than all given to one edge.
  const top = slack >= GAP * 2 ? chosen.bottom - GAP - h : Math.max(GAP, chosen.top + slack / 2);

  return { bottom: `${Math.round(vh - (Math.max(GAP, top) + h))}px` };
}
